// Core
import { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useRunOperationTaskMutation } from "state/communities/communitiesApiSlice";
import { useGetTaskStatusByIdQuery } from "state/tasks/tasksApiSlice";
import { selectActiveTasksByType } from "state/tasks/tasksSlice";

// Utils
import { handleStartTask } from "utils/handlers";
import useTaskStatusEffect from "utils/hooks/useTaskStatusEffect";

// Components
import { Crumbs, PageHead, SideTabs } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

const TINTS = {
  clearmarket: "rec-tint-green",
  settlement: "rec-tint-teal",
};

function RunOperations() {
  const { t } = useTranslation(["runOperations", "common"]);
  const community = useSelector(selectCurrentCommunity);
  // eslint-disable-next-line no-unused-vars
  const [_, setLastTaskClicked] = useState(null);
  const dispatch = useDispatch();

  const SETTLEMENT_TASK_TYPE = "PROD_RUN_SETTLEMENT";
  const CLEAR_MARKET_TASK_TYPE = "PROD_CLEAR_MARKET";

  const activeSettlementTasks = useSelector(selectActiveTasksByType(SETTLEMENT_TASK_TYPE));
  const activeClearMarketTasks = useSelector(selectActiveTasksByType(CLEAR_MARKET_TASK_TYPE));

  const returnActiveTasks = () => {
    if (activeSettlementTasks.length > 0) {
      return activeSettlementTasks;
    }
    if (activeClearMarketTasks.length > 0) {
      return activeClearMarketTasks;
    }
    return [];
  };

  // GET Task Status
  const {
    data: taskStatus,
    isFetching,
    isSuccess,
  } = useGetTaskStatusByIdQuery(
    {
      taskId: returnActiveTasks()[0]?.id,
      communityId: community.id,
      groupTask: returnActiveTasks()[0]?.groupTask ? "true" : "false",
    },
    {
      pollingInterval: 3000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skip: returnActiveTasks().length === 0,
    }
  );

  // Clear Market
  const [runClearMarket] = useRunOperationTaskMutation();
  const lastClearMarketTime = localStorage.getItem("lastProdClearMarketTime");
  const handleRunClearMarket = () => {
    localStorage.setItem("lastProdClearMarketTime", Date.now().toString());
    handleStartTask(
      runClearMarket,
      { communityId: community.id, taskName: "clear_market" },
      dispatch,
      CLEAR_MARKET_TASK_TYPE
    )();
    setLastTaskClicked(CLEAR_MARKET_TASK_TYPE);
  };

  // Run Settlement
  const [runSettlement] = useRunOperationTaskMutation();
  const lastSettlementTime = localStorage.getItem("lastProdSettlementTime");
  const handleRunSettlement = () => {
    localStorage.setItem("lastProdSettlementTime", Date.now().toString());
    handleStartTask(
      runSettlement,
      { communityId: community.id, taskName: "run_settlement" },
      dispatch,
      SETTLEMENT_TASK_TYPE,
      false
    )();
    setLastTaskClicked(SETTLEMENT_TASK_TYPE);
  };

  useTaskStatusEffect({
    isSuccess,
    isFetching,
    taskStatus,
    activeLoadsTasks: returnActiveTasks(),
    dispatch,
    type: returnActiveTasks()[0]?.type,
  });

  const isLoading = returnActiveTasks().length > 0;

  const operations = [
    {
      id: "clearmarket",
      icon: "storefront",
      title: t("clearmarket.button"),
      description: t("clearmarket.helptext"),
      onClick: handleRunClearMarket,
      timestamp: lastClearMarketTime,
      busy: activeClearMarketTasks.length > 0,
    },
    {
      id: "settlement",
      icon: "receipt_long",
      title: t("settlement.button"),
      description: t("settlement.helptext"),
      onClick: handleRunSettlement,
      timestamp: lastSettlementTime,
      busy: activeSettlementTasks.length > 0,
    },
  ];

  return (
    <div>
      <Crumbs items={[{ label: community?.name || "—" }, { label: t("headerTitle") }]} />
      <PageHead title={t("headerTitle")} />

      <SideTabs
        items={[{ id: "run-operations-control", icon: "play_circle", label: t("headerTitle") }]}
        active="run-operations-control"
        onChange={() => undefined}
      >
        <div id="run-operations-control" className="rec-card overflow-visible">
          <div className="rec-sim-head">
            <div className="rec-sim-head-txt">
              <h2>{t("headerTitle")}</h2>
              <p>{t("headerDescription")}</p>
            </div>
          </div>

          <div className="rec-card-pad">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-rec">
              {operations.map((operation) => (
                <div key={operation.id} className="rec-op-card">
                  <div className={`rec-op-ic ${TINTS[operation.id]}`}>
                    <Icon name={operation.icon} fill />
                  </div>

                  <div className="rec-op-txt">
                    <b>{operation.title}</b>
                    <p>{operation.description}</p>
                    <div className="rec-op-meta">
                      <Icon name="schedule" style={{ fontSize: 15 }} />
                      {`${t("common:lastran")}: `}
                      <b>
                        {operation.timestamp
                          ? moment(parseInt(operation.timestamp, 10)).fromNow()
                          : "--"}
                      </b>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="rec-op-btn"
                    disabled={isLoading}
                    onClick={operation.onClick}
                  >
                    {operation.busy ? (
                      <span className="rec-spin">
                        <Icon name="progress_activity" />
                      </span>
                    ) : (
                      <Icon name="play_arrow" fill />
                    )}
                    {t("common:run")}
                  </button>
                </div>
              ))}
            </div>

            <div
              className="rec-card rec-card-pad mt-rec flex items-center gap-[14px]"
              style={{ background: "var(--surface-2)", borderStyle: "dashed" }}
            >
              <Icon name="info" style={{ fontSize: 22, color: "var(--accent)" }} />
              <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.55 }}>
                {t("infoNote")}
              </div>
            </div>
          </div>
        </div>
      </SideTabs>
    </div>
  );
}

export default RunOperations;
